import { useEffect } from "react";
import { type Control, useController, useWatch } from "react-hook-form";

import type { CustomerInfoInput, SignersInput } from "../Signers.schema";

// ---------------------------------------------------------------------------
// useMirrorAddress
//
// Encapsulates the "Same as current" physical→mailing mirror logic for a single
// person column. Watches all six physical fields and the mailingSameAsCurrent
// checkbox; when the checkbox is checked the mailing fields are kept in sync
// via useEffect. Mailing inputs are disabled in the UI while the checkbox is
// checked (that disabled prop is driven by the returned `mailingSameAsCurrent`
// value in the parent component). Unchecking clears all six mailing fields
// (see `onSameAsCurrentChange`) rather than leaving the mirrored/hydrated
// values in place, so the analyst always enters a mailing address from blank.
//
// Returns `mailingSameAsCurrent` (boolean) so the caller can both disable the
// mailing AddressFields and render the checkbox checked state.
// ---------------------------------------------------------------------------

type PersonPath = "driver" | "secondSigner";

/**
 * Builds a typed RHF field path into a person's customerInfo.
 * @param {PersonPath} personPath - Which person column (driver / secondSigner).
 * @param {keyof CustomerInfoInput} field - The customerInfo field name.
 * @returns {string} The dotted field path.
 */
function getCustomerFieldPath(
  personPath: PersonPath,
  field: keyof CustomerInfoInput
): `driver.customerInfo.${keyof CustomerInfoInput}` | `secondSigner.customerInfo.${keyof CustomerInfoInput}` {
  return `${personPath}.customerInfo.${field}` as
    | `driver.customerInfo.${keyof CustomerInfoInput}`
    | `secondSigner.customerInfo.${keyof CustomerInfoInput}`;
}

interface UseMirrorAddressOptions {
  control: Control<SignersInput>;
  personPath: PersonPath;
}

interface UseMirrorAddressResult {
  /** Current value of the mailingSameAsCurrent checkbox — use to disable the mailing AddressFields. */
  mailingSameAsCurrent: boolean;
  /** Handler for the "Same as current" checkbox's onCheckedChange event. */
  onSameAsCurrentChange: (checked: boolean | "indeterminate") => void;
}

/**
 * Drives the "Same as current" physical→mailing address mirror for one person.
 * @param {UseMirrorAddressOptions} options - The RHF control and person path.
 * @returns {UseMirrorAddressResult} The checkbox state and its change handler.
 */
export function useMirrorAddress({ control, personPath }: UseMirrorAddressOptions): UseMirrorAddressResult {
  // Watch the checkbox and all six physical fields reactively.
  const mailingSameAsCurrent = useWatch({
    control,
    name: getCustomerFieldPath(personPath, "mailingSameAsCurrent"),
  });

  const physicalStreetNumber = useWatch({ control, name: getCustomerFieldPath(personPath, "physicalStreetNumber") });
  const physicalStreetName = useWatch({ control, name: getCustomerFieldPath(personPath, "physicalStreetName") });
  const physicalApartmentNumber = useWatch({
    control,
    name: getCustomerFieldPath(personPath, "physicalApartmentNumber"),
  });
  const physicalZip = useWatch({ control, name: getCustomerFieldPath(personPath, "physicalZip") });
  const physicalCity = useWatch({ control, name: getCustomerFieldPath(personPath, "physicalCity") });
  const physicalState = useWatch({ control, name: getCustomerFieldPath(personPath, "physicalState") });

  // useController instances for the six mailing fields — the only writers when
  // the checkbox is checked (AddressFields inputs are disabled in that state).
  const { field: mailingStreetNumberField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingStreetNumber"),
  });
  const { field: mailingStreetNameField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingStreetName"),
  });
  const { field: mailingApartmentNumberField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingApartmentNumber"),
  });
  const { field: mailingZipField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingZip"),
  });
  const { field: mailingCityField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingCity"),
  });
  const { field: mailingStateField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingState"),
  });
  const { field: sameAsCurrentField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingSameAsCurrent"),
  });

  // The six mailing field controllers paired with their mirror-source physical
  // value, in one place so the mirror effect and the clear-on-uncheck handler
  // below don't each separately enumerate all six fields.
  const mailingFields = [
    { field: mailingStreetNumberField, physicalValue: physicalStreetNumber },
    { field: mailingStreetNameField, physicalValue: physicalStreetName },
    { field: mailingApartmentNumberField, physicalValue: physicalApartmentNumber },
    { field: mailingZipField, physicalValue: physicalZip },
    { field: mailingCityField, physicalValue: physicalCity },
    { field: mailingStateField, physicalValue: physicalState },
  ];

  // When checked, keep mailing fields in sync with physical reactively.
  useEffect(() => {
    if (!mailingSameAsCurrent) {
      return;
    }
    for (const { field, physicalValue } of mailingFields) {
      field.onChange(physicalValue ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately tracking the six physical values + checkbox; mailingFields is rebuilt every render from the same six controllers
  }, [
    mailingSameAsCurrent,
    physicalStreetNumber,
    physicalStreetName,
    physicalApartmentNumber,
    physicalZip,
    physicalCity,
    physicalState,
  ]);

  /**
   * Handles the "Same as current" checkbox toggle. Checking it lets the
   * mirroring effect above take over reactively; unchecking clears every
   * mailing field back to blank — the analyst re-enters a genuinely distinct
   * mailing address rather than editing a stale mirrored (or previously
   * hydrated) copy of physical.
   * @param {boolean | "indeterminate"} checked - The checkbox's next checked state.
   */
  function onSameAsCurrentChange(checked: boolean | "indeterminate") {
    const isChecked = checked === true;
    sameAsCurrentField.onChange(isChecked);
    if (isChecked) {
      // The useEffect above handles the actual mirroring reactively.
      return;
    }
    for (const { field } of mailingFields) {
      field.onChange("");
    }
  }

  return { mailingSameAsCurrent: Boolean(mailingSameAsCurrent), onSameAsCurrentChange };
}
